import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight, Play } from "lucide-react";
import { catLabel, catAccent, timeAgo, readTime } from "@/lib/api";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ featured, bulletin }) {
  if (!featured) return null;
  const titleWords = featured.title.split(" ");
  const mid = Math.ceil(titleWords.length / 2);
  const lines = [titleWords.slice(0, mid).join(" "), titleWords.slice(mid).join(" ")];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      <motion.div
        data-testid="hero-featured-article"
        className="lg:col-span-8 relative group"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <Link to={`/article/${featured.id}`} className="block relative overflow-hidden border border-white/10">
          <motion.div
            className="relative aspect-[16/10] sm:aspect-[16/9] min-h-[440px] sm:min-h-[540px]"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {featured.youtube_video_id && (
              <img
                src={featured.image_url}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover blur-3xl scale-125 opacity-50"
              />
            )}
            <img
              src={featured.image_url}
              alt={featured.title}
              className={`relative w-full h-full transition-transform duration-[1.2s] group-hover:scale-[1.04] ${
                featured.youtube_video_id ? "object-contain" : "object-cover"
              }`}
            />
            {featured.youtube_video_id && (
              <span className="absolute inset-0 flex items-center justify-center" data-testid="hero-play-badge">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#FF375F] text-white shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={24} fill="currentColor" className="ml-0.5" />
                </span>
              </span>
            )}
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D10] via-[#0D0D10]/85 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[#0D0D10] via-[#0D0D10]/95 to-transparent" />
          <div className="force-dark absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="flex items-center gap-3 mb-4"
            >
              <span className="bg-[#FF453A] text-white font-mono-meta text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> मुख्य समाचार
              </span>
              <span
                className="font-mono-meta text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 bg-[#0D0D10]/70 backdrop-blur"
                style={{ color: catAccent(featured.category) }}
              >
                {catLabel(featured.category)}
              </span>
            </motion.div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-[3.4rem] leading-[1.2] text-white max-w-3xl">
              {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span className="block" custom={i} variants={lineReveal} initial="hidden" animate="show">
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-4 flex flex-wrap items-center gap-4 font-mono-meta text-xs text-slate-300"
            >
              <span className="text-[#FFD400] font-semibold">{featured.author}</span>
              <span className="flex items-center gap-1.5"><Clock size={12} /> {timeAgo(featured.created_at)}</span>
              <span>{readTime(featured.content)} पढ़ने का समय</span>
              <span className="ml-auto hidden sm:flex items-center gap-1 text-white group-hover:text-[#FFD400] transition-colors">
                पूरी खबर <ArrowUpRight size={14} />
              </span>
            </motion.div>
          </div>
        </Link>
      </motion.div>

      <motion.aside
        className="lg:col-span-4 bg-[#15151A] border border-white/[0.08] flex flex-col"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-bulletin"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-editorial font-black text-white text-lg">खबरिक बुलेटिन</h2>
          <span className="font-mono-meta text-[10px] text-[#FFD400] uppercase tracking-widest">Top 05</span>
        </div>
        <div className="divide-y divide-white/[0.06]">
          {bulletin.slice(0, 5).map((a, i) => (
            <Link
              key={a.id}
              to={`/article/${a.id}`}
              className="flex gap-4 px-5 py-4 group hover:bg-white/[0.03] transition-colors duration-200"
            >
              <span className="font-display text-2xl text-stroke-yellow group-hover:text-[#FFD400] group-hover:[-webkit-text-stroke:0px] transition-all leading-none pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="text-sm font-semibold text-slate-200 leading-snug group-hover:text-white line-clamp-2">
                  {a.title}
                </h3>
                <span className="font-mono-meta text-[10px] text-slate-500 mt-1 block">
                  {catLabel(a.category)} · {timeAgo(a.created_at)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </motion.aside>
    </section>
  );
}
